import {
  BoardCirculationListResponseSchema,
  BoardCirculationAnnotationSchema,
  BoardCirculationSchema,
  BoardActionSchema,
  BoardMeetingListResponseSchema,
  BoardMeetingSchema,
  BoardMemoSchema,
  BoardMinutesSchema,
  BoardPaperSchema,
  BoardPackBuilderSchema,
  BoardPackVersionDiffSchema,
  BoardPackSectionSchema,
  BoardResolutionSchema,
} from "@l30/shared";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiFetchUnchecked } from "./client.js";

import type {
  BoardAction,
  BoardActionUpdate,
  BoardCirculation,
  BoardCirculationCreate,
  BoardCirculationListResponse,
  BoardMeeting,
  BoardMeetingCreate,
  BoardMeetingUpdate,
  BoardMemo,
  BoardMemoCreate,
  BoardMemoActionCreate,
  BoardMemoResolutionCreate,
  BoardMemoUpdate,
  BoardMinutes,
  BoardMinutesUpsert,
  BoardPaper,
  BoardPaperCreate,
  BoardPaperUpdate,
  BoardCirculationAnnotation,
  BoardCirculationAnnotationCreate,
  BoardPackBuilder,
  BoardPackVersionDiff,
  BoardPackSection,
  BoardPackSectionUpdate,
  BoardResolution,
  BoardResolutionCreate,
  BoardResolutionUpdate,
} from "@l30/shared";

const MEETINGS_KEY = ["board-meetings"] as const;

export function useBoardMeetings(status?: string, options?: { enabled?: boolean }) {
  const params = status ? `?status=${status}` : "";
  return useQuery({
    queryKey: [...MEETINGS_KEY, status],
    queryFn: () => apiFetch(`/board-meetings${params}`, {}, BoardMeetingListResponseSchema),
    enabled: options?.enabled ?? true,
  });
}

export function useBoardMeeting(id: string) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, id],
    queryFn: () => apiFetch(`/board-meetings/${id}`, {}, BoardMeetingSchema),
    enabled: !!id,
  });
}

export function useBoardPackBuilder(id: string | null) {
  return useQuery({
    queryKey: [...MEETINGS_KEY, id, "pack-builder"],
    queryFn: () => apiFetch(`/board-meetings/${id}/pack-builder`, {}, BoardPackBuilderSchema),
    enabled: !!id,
  });
}

export function useCreateBoardMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: BoardMeetingCreate) =>
      apiFetchUnchecked<BoardMeeting>("/board-meetings", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useUpdateBoardMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: BoardMeetingUpdate & { id: string }) =>
      apiFetchUnchecked<BoardMeeting>(`/board-meetings/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useApplyStandardBoardPackTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetch<BoardPackBuilder>(
        `/board-meetings/${meetingId}/template/standard`,
        { method: "POST" },
        BoardPackBuilderSchema,
      ),
    onSuccess: (_data, meetingId) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
    },
  });
}

export function useUpdateBoardPackSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      sectionKey,
      data,
    }: {
      meetingId: string;
      sectionKey: string;
      data: BoardPackSectionUpdate;
    }) =>
      apiFetch<BoardPackSection>(
        `/board-meetings/${meetingId}/pack-sections/${sectionKey}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        BoardPackSectionSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useRefreshBoardCommercialPipelineSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetch<BoardPackSection>(
        `/board-meetings/${meetingId}/pack-sections/commercial-pipeline/refresh-crm`,
        { method: "POST" },
        BoardPackSectionSchema,
      ),
    onSuccess: (_data, meetingId) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
    },
  });
}

export function useRefreshBoardProductRoadmapSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetch<BoardPackSection>(
        `/board-meetings/${meetingId}/pack-sections/product-roadmap/refresh-product-roadmap`,
        { method: "POST" },
        BoardPackSectionSchema,
      ),
    onSuccess: (_data, meetingId) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
    },
  });
}

export function useRefreshBoardFinanceSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetch<BoardPackSection>(
        `/board-meetings/${meetingId}/pack-sections/financial-performance/refresh-finance`,
        { method: "POST" },
        BoardPackSectionSchema,
      ),
    onSuccess: (_data, meetingId) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
    },
  });
}

export function useRefreshBoardPeopleSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetch<BoardPackSection>(
        `/board-meetings/${meetingId}/pack-sections/people-update/refresh-people`,
        { method: "POST" },
        BoardPackSectionSchema,
      ),
    onSuccess: (_data, meetingId) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
    },
  });
}

export function useCreateBoardMemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: BoardMemoCreate }) =>
      apiFetch<BoardMemo>(
        `/board-meetings/${meetingId}/memos`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardMemoSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useUpdateBoardMemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      memoId,
      data,
    }: {
      meetingId: string;
      memoId: string;
      data: BoardMemoUpdate;
    }) =>
      apiFetch<BoardMemo>(
        `/board-meetings/${meetingId}/memos/${memoId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        BoardMemoSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useMarkBoardMemoReady() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, memoId }: { meetingId: string; memoId: string }) =>
      apiFetch<BoardMemo>(
        `/board-meetings/${meetingId}/memos/${memoId}/mark-ready`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
        BoardMemoSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useApproveBoardMemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, memoId }: { meetingId: string; memoId: string }) =>
      apiFetch<BoardMemo>(
        `/board-meetings/${meetingId}/memos/${memoId}/approve`,
        {
          method: "POST",
          body: JSON.stringify({}),
        },
        BoardMemoSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useCreateBoardMemoResolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      memoId,
      data,
    }: {
      meetingId: string;
      memoId: string;
      data: BoardMemoResolutionCreate;
    }) =>
      apiFetch<BoardResolution>(
        `/board-meetings/${meetingId}/memos/${memoId}/create-resolution`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardResolutionSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useCreateBoardMemoAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      memoId,
      data,
    }: {
      meetingId: string;
      memoId: string;
      data: BoardMemoActionCreate;
    }) =>
      apiFetchUnchecked(`/board-meetings/${meetingId}/memos/${memoId}/create-action`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useCreateBoardResolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: BoardResolutionCreate }) =>
      apiFetch<BoardResolution>(
        `/board-meetings/${meetingId}/resolutions`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardResolutionSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useUpdateBoardResolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      resolutionId,
      data,
    }: {
      meetingId: string;
      resolutionId: string;
      data: BoardResolutionUpdate;
    }) =>
      apiFetch<BoardResolution>(
        `/board-meetings/${meetingId}/resolutions/${resolutionId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        BoardResolutionSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useUpdateBoardAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      actionId,
      data,
    }: {
      meetingId: string;
      actionId: string;
      data: BoardActionUpdate;
    }) =>
      apiFetch<BoardAction>(
        `/board-meetings/${meetingId}/actions/${actionId}`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        },
        BoardActionSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: MEETINGS_KEY });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useCreateBoardPaper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: BoardPaperCreate }) =>
      apiFetch<BoardPaper>(
        `/board-meetings/${meetingId}/papers`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardPaperSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useUpdateBoardPaper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      paperId,
      data,
    }: {
      meetingId: string;
      paperId: string;
      data: BoardPaperUpdate;
    }) =>
      apiFetch<BoardPaper>(
        `/board-meetings/${meetingId}/papers/${paperId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
        BoardPaperSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useDeleteBoardPaper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, paperId }: { meetingId: string; paperId: string }) =>
      apiFetchUnchecked<void>(`/board-meetings/${meetingId}/papers/${paperId}`, {
        method: "DELETE",
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useRecordBoardCirculation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: BoardCirculationCreate }) =>
      apiFetch<BoardCirculationListResponse>(
        `/board-meetings/${meetingId}/circulations`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardCirculationListResponseSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useMarkBoardCirculationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, circulationId }: { meetingId: string; circulationId: string }) =>
      apiFetch<BoardCirculation>(
        `/board-meetings/${meetingId}/circulations/${circulationId}/mark-read`,
        { method: "POST", body: JSON.stringify({}) },
        BoardCirculationSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useMarkBoardCirculationDownloaded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, circulationId }: { meetingId: string; circulationId: string }) =>
      apiFetch<BoardCirculation>(
        `/board-meetings/${meetingId}/circulations/${circulationId}/mark-downloaded`,
        { method: "POST", body: JSON.stringify({}) },
        BoardCirculationSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useAcknowledgeBoardCirculation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, circulationId }: { meetingId: string; circulationId: string }) =>
      apiFetch<BoardCirculation>(
        `/board-meetings/${meetingId}/circulations/${circulationId}/acknowledge`,
        { method: "POST", body: JSON.stringify({}) },
        BoardCirculationSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useCreateBoardCirculationAnnotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      circulationId,
      data,
    }: {
      meetingId: string;
      circulationId: string;
      data: BoardCirculationAnnotationCreate;
    }) =>
      apiFetch<BoardCirculationAnnotation>(
        `/board-meetings/${meetingId}/circulations/${circulationId}/annotations`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        BoardCirculationAnnotationSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useUpsertBoardMinutes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, data }: { meetingId: string; data: BoardMinutesUpsert }) =>
      apiFetch<BoardMinutes>(
        `/board-meetings/${meetingId}/minutes`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        },
        BoardMinutesSchema,
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, variables.meetingId, "pack-builder"] });
    },
  });
}

export function useApproveBoardMinutes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetch<BoardMinutes>(
        `/board-meetings/${meetingId}/minutes/approve`,
        { method: "POST", body: JSON.stringify({}) },
        BoardMinutesSchema,
      ),
    onSuccess: (_data, meetingId) => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
    },
  });
}

export function useDeleteBoardMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<void>(`/board-meetings/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

/**
 * Finalise a working-paper draft for this meeting.
 *
 * The server-side handler (Task 10):
 *  1. Promotes the workspace document's current version into the Vault
 *     (under `vaultFolderId`)
 *  2. Creates a board_papers row linking the new vault document to this
 *     meeting (optionally under `sectionKey`)
 *  3. Marks the workspace document status = "final"
 *
 * On success we invalidate:
 *  - The board-meeting detail key → pack-builder refreshes (new paper appears)
 *  - The whole ["workspaces"] prefix → working-papers panel document list refreshes
 */
export function useFinaliseWorkingPaper(meetingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      workspaceDocumentId: string;
      vaultFolderId: string;
      sectionKey?: string | null;
    }) =>
      apiFetchUnchecked(`/board-meetings/${meetingId}/finalise-working-paper`, {
        method: "POST",
        body: JSON.stringify(args),
      }),
    onSuccess: () => {
      // Refresh the board pack builder (the new paper should appear there)
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId] });
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, meetingId, "pack-builder"] });
      // Refresh the workspace document lists so the working-papers panel shows
      // the updated status (final) and the document is no longer shown as a draft.
      qc.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
}

export function useTransitionMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetStatus }: { id: string; targetStatus: string }) =>
      apiFetchUnchecked<BoardMeeting>(`/board-meetings/${id}/transition`, {
        method: "POST",
        body: JSON.stringify({ targetStatus }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useTakeSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetchUnchecked(`/board-meetings/${meetingId}/snapshot`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useGenerateCommentary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetchUnchecked(`/board-meetings/${meetingId}/commentary`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useGeneratePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiFetchUnchecked(`/board-meetings/${meetingId}/pack`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: MEETINGS_KEY }),
  });
}

export function useDownloadPack() {
  return useMutation({
    mutationFn: async (meetingId: string) => {
      const result = await apiFetchUnchecked<{ downloadUrl: string }>(
        `/board-meetings/${meetingId}/pack/download`,
      );
      window.open(result.downloadUrl, "_blank");
      return result;
    },
  });
}

export function useMeetingVersions(meetingId: string | null, type?: string) {
  const params = type ? `?type=${type}` : "";
  return useQuery({
    queryKey: [...MEETINGS_KEY, meetingId, "versions", type],
    queryFn: () =>
      apiFetchUnchecked<{
        assets: Array<{
          id: string;
          type: string;
          version: number;
          status: string;
          createdAt: string;
        }>;
      }>(`/board-meetings/${meetingId}/versions${params}`),
    enabled: Boolean(meetingId),
  });
}

export function useBoardPackVersionDiff(
  meetingId: string | null,
  options: { fromAssetId?: string; toAssetId?: string; enabled?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (options.fromAssetId) params.set("fromAssetId", options.fromAssetId);
  if (options.toAssetId) params.set("toAssetId", options.toAssetId);
  const query = params.toString();

  return useQuery({
    queryKey: [...MEETINGS_KEY, meetingId, "pack-diff", options.fromAssetId, options.toAssetId],
    queryFn: () =>
      apiFetch<BoardPackVersionDiff>(
        `/board-meetings/${meetingId}/pack/diff${query ? `?${query}` : ""}`,
        {},
        BoardPackVersionDiffSchema,
      ),
    enabled: Boolean(meetingId) && (options.enabled ?? true),
  });
}
